import React, { useState, useEffect, useRef, useMemo } from 'react';
import { parseAddress, extractEmail, parseAddressList } from '../utils/emailParsing';
import EmailAttachmentBar from '../../../components/EmailAttachmentBar';
import useSentRecipients from '../../EmailAiModes/useSentRecipients';
import SendConfirmModal from '../../EmailAiModes/SendConfirmModal';

/**
 * InlineComposer — the Gmail-style inline reply box that lives at the bottom of
 * the reading pane. Manual writing is the primary flow; "AI Assist" fills the
 * textarea with a generated draft the user can edit before sending.
 *
 * It is controlled by the parent for open/close, but owns its own draft text,
 * subject, selected sender account and file attachments.
 */
export default function InlineComposer({
  userId,
  originalEmail,
  accounts,
  sending,
  generating,
  closing = false,
  initialText = '',
  initialTo = '',
  initialForwardTo = '',
  initialForwardScope = 'single',
  mode = 'reply',
  threadMessages = [],
  onSend,
  onGenerate,
  onRequestClose,
  onForceClose,
  onDirtyChange,
  onCheckRecipient,
}) {
  const isForward = mode === 'forward';
  const isNew = mode === 'new';
  const sender = parseAddress(originalEmail?.from);

  const baseSubject = originalEmail?.subject || '';
  const initialSubject = isNew ? '' : isForward
    ? (baseSubject.toLowerCase().startsWith('fwd:') ? baseSubject : `Fwd: ${baseSubject}`)
    : (baseSubject.toLowerCase().startsWith('re:') ? baseSubject : `Re: ${baseSubject}`);

  // Seeded with a Reply-Agent draft when one exists (else empty).
  const [text, setText] = useState(initialText || '');
  const [subject, setSubject] = useState(initialSubject);
  const [accountId, setAccountId] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState('');
  // Forward only: the free-text destination address + its validation error.
  const [forwardTo, setForwardTo] = useState(initialForwardTo || '');
  const [forwardError, setForwardError] = useState('');
  // Forward only: which messages to embed — the whole conversation or just the
  // single selected message. Only relevant when the thread has >1 message.
  const [forwardScope, setForwardScope] = useState(initialForwardScope); // 'single' | 'thread'
  const threadCount = Array.isArray(threadMessages) ? threadMessages.length : 0;
  const canChooseScope = isForward && threadCount > 1;
  // New email only: free-text recipient. Enter runs a DB lookup — a domain hit
  // is offered as a link (dedup), an exact email hit is silently accepted.
  const [newTo, setNewTo] = useState(initialTo || '');
  const [newToError, setNewToError] = useState('');
  const [checkingTo, setCheckingTo] = useState(false);
  const [checkedTo, setCheckedTo] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [linkedContact, setLinkedContact] = useState(null);
  const textRef = useRef(null);
  // Any DB contact the checked address resolves to (email or domain match),
  // used to surface prior sends even when the user ignores the link offer.
  const [historyContactId, setHistoryContactId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const {
    sentCount, lastSent, sentMessages, loading: historyLoading,
  } = useSentRecipients(isNew ? userId : null, historyContactId, isNew && checkedTo ? [checkedTo] : []);
  const attachmentInputRef = useRef(null);
  // Rapid-fire guard. Sending is now fire-and-forget (the composer usually
  // closes synchronously after onSend), so we can't rely on a `sending` prop
  // to disable this button between the click and the unmount. A short
  // time-based debounce swallows a stray double-click without permanently
  // locking the button if the composer happens to stay open (e.g. an
  // enqueue rejection due to a suddenly-missing account).
  const lastSendAtRef = useRef(0);

  // Addresses a forward must NEVER go to: the original sender, the mailbox that
  // received it, anyone on the original To line, and any of our own connected
  // accounts. This is the core guard — a forward is meant for a NEW third party.
  const excludeSet = useMemo(() => {
    const set = new Set();
    const add = (raw) => {
      const e = extractEmail(raw) || (raw && raw.includes('@') ? raw.toLowerCase().trim() : '');
      if (e) set.add(e);
    };
    add(originalEmail?.from);
    if (originalEmail?.accountEmail) set.add(originalEmail.accountEmail.toLowerCase().trim());
    String(originalEmail?.to || '').split(/[,;]+/).forEach(add);
    String(originalEmail?.recipients || '').split(/[,;]+/).forEach(add);
    (accounts || []).forEach((a) => { if (a.email) set.add(a.email.toLowerCase().trim()); });
    return set;
  }, [originalEmail, accounts]);

  // Reply-All prefill: everyone who was on the original conversation (original
  // CC + other To recipients) stays looped in on the reply. We strip our own
  // mailboxes (any connected account, plus the mailbox that received the mail)
  // and the original sender (already on the To line) so nobody gets a duplicate.
  const replyAllCc = useMemo(() => {
    if (isForward) return '';
    if (!originalEmail) return '';
    const own = new Set();
    (accounts || []).forEach((a) => { if (a.email) own.add(a.email.toLowerCase().trim()); });
    if (originalEmail.accountEmail) own.add(originalEmail.accountEmail.toLowerCase().trim());
    const senderEmail = (parseAddress(originalEmail.from).email || '').toLowerCase();
    if (senderEmail) own.add(senderEmail);

    const combined = [
      ...parseAddressList(originalEmail.cc),
      ...parseAddressList(originalEmail.to),
      ...parseAddressList(originalEmail.recipients),
    ];
    const seen = new Set();
    const keep = [];
    combined.forEach((addr) => {
      if (own.has(addr) || seen.has(addr)) return;
      seen.add(addr);
      keep.push(addr);
    });
    return keep.join(', ');
  }, [isForward, originalEmail, accounts]);

  // Cc / Bcc are hidden until the user reveals them (subtle, Gmail-style).
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');

  // Pick a sensible default sender: the account that received the original mail.
  useEffect(() => {
    if (!accounts.length) return;
    const match = accounts.find((a) => a.id === originalEmail?.accountId)
      || accounts.find((a) => a.email === originalEmail?.accountEmail)
      || accounts[0];
    setAccountId(match?.id || '');
  }, [accounts, originalEmail]);

  // Reset draft (incl. attachments, cc/bcc) when switching source emails or
  // mode. Seed with the agent draft if one was passed for this email. Reply
  // mode also seeds CC with everyone who was on the original conversation
  // (reply-all default) — the CC row auto-expands when there's something to
  // show, so the user sees at a glance who's still looped in.
  useEffect(() => {
    setText(initialText || '');
    setSubject(initialSubject);
    setAttachments([]);
    setAttachError('');
    setForwardTo(initialForwardTo || '');
    setForwardError('');
    setForwardScope(initialForwardScope);
    setShowBcc(false);
    setBcc('');
    if (!isForward && replyAllCc) {
      setCc(replyAllCc);
      setShowCc(true);
    } else {
      setCc('');
      setShowCc(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalEmail?.id, mode]);

  const account = accounts.find((a) => a.id === accountId) || null;
  // Reply → fixed to the original sender. Forward / new → free-text destination.
  const recipient = isNew ? newTo.trim().toLowerCase() : isForward ? forwardTo.trim() : sender.email;

  const handleNewToChange = (value) => {
    setNewTo(value);
    if (newToError) setNewToError('');
    if (value.trim().toLowerCase() !== checkedTo) {
      setSuggestion(null);
      setLinkedContact(null);
      setHistoryContactId(null);
      setCheckedTo('');
    }
  };

  const handleNewToKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const to = newTo.trim().toLowerCase();
    if (!/.+@.+\..+/.test(to)) {
      setNewToError('Please enter a valid email address.');
      return;
    }
    if (to === checkedTo || !onCheckRecipient) {
      setCheckedTo(to);
      textRef.current?.focus();
      return;
    }
    setCheckingTo(true);
    try {
      const match = await onCheckRecipient(to);
      setHistoryContactId(match?.docId || null);
      if (match?.matchType === 'email') {
        setLinkedContact({ docId: match.docId, name: match.name });
      } else if (match?.matchType === 'domain') {
        setSuggestion(match);
      }
    } catch (err) {
      console.warn('[InlineComposer] recipient DB check failed (non-fatal):', err?.message);
    } finally {
      setCheckedTo(to);
      setCheckingTo(false);
    }
    textRef.current?.focus();
  };

  // "Dirty" = there is input worth protecting. An untouched, pre-filled agent
  // draft does NOT count (so flipping between mails with ready replies never
  // nags); the same applies to the reply-all CC seed — only user edits on
  // top of it (or attachments / bcc / forward destination) count as dirty.
  const isDirty = text.trim() !== (initialText || '').trim()
    || (Array.isArray(attachments) && attachments.length > 0)
    || (isForward && forwardTo.trim().length > 0)
    || (isNew && (newTo.trim().length > 0 || subject.trim().length > 0))
    || cc.trim() !== (replyAllCc || '').trim()
    || bcc.trim().length > 0;

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // On unmount the draft is gone — make sure the parent's dirty flag clears.
  useEffect(() => () => {
    if (onDirtyChange) onDirtyChange(false);
  }, [onDirtyChange]);

  const handleGenerate = async () => {
    const generated = await onGenerate({ originalEmail, account });
    if (generated) setText(generated);
  };

  // Split a free-text Cc/Bcc field into individual, roughly-valid addresses.
  const parseAddresses = (raw) =>
    (raw || '')
      .split(/[,;\s]+/)
      .map((a) => a.trim())
      .filter((a) => /.+@.+\..+/.test(a));

  // Forward guard: valid address that is NOT the original sender / our mailbox.
  const validateForwardRecipient = () => {
    const to = forwardTo.trim().toLowerCase();
    if (!/.+@.+\..+/.test(to)) {
      setForwardError('Please enter a valid email address.');
      return false;
    }
    if (excludeSet.has(to)) {
      setForwardError('You can’t forward to the original sender or one of your own mailboxes — pick a different address.');
      return false;
    }
    setForwardError('');
    return true;
  };

  const handleSend = ({ historyConfirmed = false } = {}) => {
    if (isNew && sentCount > 0 && !historyConfirmed) {
      setConfirmSendOpen(true);
      return;
    }
    // Swallow a stray double-click (see lastSendAtRef comment above).
    if (Date.now() - lastSendAtRef.current < 1000) return;

    // 📎 Attachments must be fully uploaded before we hand off to the backend.
    if (EmailAttachmentBar.hasPending(attachments)) {
      setAttachError('Please wait for attachments to finish uploading.');
      return;
    }
    if (EmailAttachmentBar.hasErrors(attachments)) {
      setAttachError('One or more attachments failed to upload. Remove them or retry.');
      return;
    }
    if (isForward && !validateForwardRecipient()) return;
    if (isNew && !/.+@.+\..+/.test(recipient)) {
      setNewToError('Please enter a valid email address.');
      return;
    }
    setAttachError('');
    lastSendAtRef.current = Date.now();

    // Which messages get embedded in a forward: the whole conversation, or just
    // the single selected message. (Reply ignores this.)
    let forwardMessages;
    if (isForward) {
      forwardMessages = (forwardScope === 'thread' && threadCount > 1)
        ? threadMessages
        : [originalEmail];
    }

    onSend({
      mode,
      account,
      originalEmail,
      forwardMessages,
      text,
      subject,
      recipients: [recipient],
      leadId: isNew ? (linkedContact?.docId || null) : undefined,
      cc: parseAddresses(cc),
      bcc: parseAddresses(bcc),
      // Only metadata; the cloud function pulls bytes from Storage before send.
      attachments: EmailAttachmentBar.toRequestPayload(attachments),
    });
  };

  return (
    <div className={`ivx-composer ${closing ? 'closing' : ''}`}>
      <div className="ivx-composer-head">
        <div className="ivx-composer-fields">
          <div className="ivx-composer-field">
            <span className="ivx-composer-label">To</span>
            {isNew ? (
              <input
                className="ivx-composer-toinput"
                type="email"
                value={newTo}
                onChange={(e) => handleNewToChange(e.target.value)}
                onKeyDown={handleNewToKeyDown}
                placeholder="name@example.com — press Enter to check"
                autoFocus
              />
            ) : isForward ? (
              <input
                className="ivx-composer-toinput"
                type="email"
                value={forwardTo}
                onChange={(e) => { setForwardTo(e.target.value); if (forwardError) setForwardError(''); }}
                placeholder="Forward to… name@example.com"
                autoFocus
              />
            ) : (
              <span className="ivx-composer-to">{recipient || 'Unknown'}</span>
            )}
            <span className="ivx-composer-ccbcc">
              {!showCc && (
                <button type="button" className="ivx-ccbcc-toggle" onClick={() => setShowCc(true)}>
                  Cc
                </button>
              )}
              {!showBcc && (
                <button type="button" className="ivx-ccbcc-toggle" onClick={() => setShowBcc(true)}>
                  Bcc
                </button>
              )}
            </span>
          </div>
          {isForward && forwardError && (
            <div className="ivx-composer-field">
              <span className="ivx-composer-label" />
              <span className="ivx-composer-forward-error">{forwardError}</span>
            </div>
          )}

          {isNew && (newToError || checkingTo || suggestion || linkedContact || sentCount > 0) && (
            <div className="ivx-composer-field">
              <span className="ivx-composer-label" />
              {newToError && <span className="ivx-composer-forward-error">{newToError}</span>}
              {!newToError && checkingTo && <span className="ivx-newmail-note"><span className="ivx-spinner" /> Checking your database…</span>}
              {!newToError && !checkingTo && sentCount > 0 && (
                <span className="ivx-newmail-note ivx-newmail-warn">
                  Already emailed {sentCount} {sentCount === 1 ? 'time' : 'times'} (this contact / domain).
                  <button type="button" className="ivx-ccbcc-toggle" onClick={() => setHistoryOpen(true)}>View history</button>
                </span>
              )}
              {!newToError && !checkingTo && linkedContact && (
                <span className="ivx-newmail-note">
                  Linked to <strong>{linkedContact.name}</strong>
                  {!suggestion && <button type="button" className="ivx-ccbcc-toggle" onClick={() => setLinkedContact(null)}>Unlink</button>}
                </span>
              )}
              {!newToError && !checkingTo && suggestion && !linkedContact && (
                <span className="ivx-newmail-note">
                  <strong>{suggestion.name}</strong> ({suggestion.domain}) is already in your database. Match this contact to it?
                  <button
                    type="button"
                    className="ivx-ccbcc-toggle"
                    onClick={() => { setLinkedContact({ docId: suggestion.docId, name: suggestion.name }); setSuggestion(null); }}
                  >
                    Match
                  </button>
                  <button type="button" className="ivx-ccbcc-toggle" onClick={() => setSuggestion(null)}>Ignore</button>
                </span>
              )}
            </div>
          )}

          {canChooseScope && (
            <div className="ivx-composer-field">
              <span className="ivx-composer-label">Umfang</span>
              <div className="ivx-forward-scope" role="radiogroup" aria-label="Was weiterleiten">
                <button
                  type="button"
                  className={`ivx-forward-scope-opt ${forwardScope === 'single' ? 'active' : ''}`}
                  aria-pressed={forwardScope === 'single'}
                  onClick={() => setForwardScope('single')}
                >
                  Nur diese Nachricht
                </button>
                <button
                  type="button"
                  className={`ivx-forward-scope-opt ${forwardScope === 'thread' ? 'active' : ''}`}
                  aria-pressed={forwardScope === 'thread'}
                  onClick={() => setForwardScope('thread')}
                >
                  Ganzer Verlauf ({threadCount})
                </button>
              </div>
            </div>
          )}

          {showCc && (
            <div className="ivx-composer-field">
              <span className="ivx-composer-label">Cc</span>
              <input
                className="ivx-composer-ccinput"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="name@example.com"
                autoFocus
              />
            </div>
          )}

          {showBcc && (
            <div className="ivx-composer-field">
              <span className="ivx-composer-label">Bcc</span>
              <input
                className="ivx-composer-ccinput"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="name@example.com"
                autoFocus
              />
            </div>
          )}

          <div className="ivx-composer-field">
            <span className="ivx-composer-label">From</span>
            <select
              className="ivx-composer-select"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.email}</option>
              ))}
            </select>
          </div>
        </div>
        <button type="button" className="ivx-composer-close" onClick={onRequestClose} title="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <input
        className="ivx-composer-subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
      />

      <textarea
        ref={textRef}
        className="ivx-composer-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={isForward ? 'Add a message (optional)…' : isNew ? 'Write your email…' : 'Write your reply…'}
        rows={8}
        autoFocus={!isForward && !isNew}
      />

      <EmailAttachmentBar
        userId={userId}
        attachments={attachments}
        onChange={setAttachments}
        disabled={sending}
        fileInputRef={attachmentInputRef}
      />

      {attachError && <div className="ivx-composer-attach-error">{attachError}</div>}

      <div className="ivx-composer-actions">
        <div className="ivx-composer-actions-left">
          <button
            type="button"
            className="ivx-composer-attach"
            onClick={() => attachmentInputRef.current && attachmentInputRef.current.click()}
            disabled={sending}
            title="Attach files"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
            Attach
          </button>
        </div>

        <div className="ivx-composer-actions-center">
          {!isForward && !isNew && (
          <button
            type="button"
            className="ivx-composer-ai"
            onClick={handleGenerate}
            disabled={generating || sending}
          >
            {generating ? (
              <><span className="ivx-spinner" /> Generating…</>
            ) : (
              <>
                <svg className="ivx-ai-icon" width="15" height="15" viewBox="0 0 535.5 535.5" fill="currentColor" aria-hidden="true">
                  <polygon points="165.75,91.8 229.5,127.5 193.799,63.75 229.5,0 165.75,35.7 102,0 137.7,63.75 102,127.5" />
                  <polygon points="471.75,341.7 408,306 443.7,369.75 408,433.5 471.75,397.8 535.5,433.5 499.8,369.75 535.5,306" />
                  <polygon points="535.5,0 471.75,35.7 408,0 443.7,63.75 408,127.5 471.75,91.8 535.5,127.5 499.8,63.75" />
                  <path d="M341.7,135.15c-10.2-10.2-25.5-10.2-35.7,0L7.649,433.5c-10.2,10.2-10.2,25.5,0,35.7l58.65,58.649c10.2,10.2,25.5,10.2,35.7,0L400.35,229.5c10.2-10.2,10.2-25.5,0-35.7L341.7,135.15z M313.65,275.4L260.1,221.85l61.2-61.2l53.55,53.55L313.65,275.4z" />
                </svg>
                AI&nbsp;Assist
              </>
            )}
          </button>
          )}
        </div>

        <div className="ivx-composer-actions-right">
          <button type="button" className="ivx-composer-discard" onClick={onForceClose} disabled={sending}>
            Discard
          </button>
          <button
            type="button"
            className="ivx-composer-send"
            onClick={() => handleSend()}
            disabled={sending || generating || !account || (isForward ? !forwardTo.trim() : (!text.trim() || !recipient || checkingTo || (isNew && (checkedTo !== recipient || historyLoading))))}
          >
            {sending
              ? (<><span className="ivx-spinner" /> {isForward ? 'Forwarding…' : 'Sending…'}</>)
              : (isForward ? 'Forward' : 'Send')}
          </button>
        </div>
      </div>

      {isNew && (
        <SendConfirmModal
          isOpen={historyOpen || confirmSendOpen}
          variant="warning"
          recipient={recipient}
          sentCount={sentCount}
          lastSent={lastSent}
          sentMessages={sentMessages}
          title={confirmSendOpen ? 'Already contacted' : 'Email history'}
          message={`You have already sent ${sentCount} email${sentCount === 1 ? '' : 's'} to this contact or domain. Review the history before sending.`}
          confirmText={confirmSendOpen ? 'Send anyway' : 'Got it'}
          cancelText={confirmSendOpen ? 'Cancel' : 'Close'}
          onCancel={() => { setHistoryOpen(false); setConfirmSendOpen(false); }}
          onConfirm={() => {
            const wasSend = confirmSendOpen;
            setHistoryOpen(false);
            setConfirmSendOpen(false);
            if (wasSend) handleSend({ historyConfirmed: true });
          }}
        />
      )}
    </div>
  );
}
